import { useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import { OnboardingFlow } from '@/components/shared/OnboardingFlow'
import { AnalyticsNotice } from '@/components/shared/AnalyticsNotice'
import {
  LayoutDashboard,
  ShieldAlert,
  Users,
  Map,
  Newspaper,
  BarChart2,
  BookOpen,
  FileText,
  GitCompare,
  Scale,
  Settings,
  ChevronLeft,
  ChevronRight,
  Shield,
  Menu,
  LogOut,
  Skull,
  Cloud,
  CalendarDays,
  Crosshair,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { campaignsSurfaceEnabled } from '@/lib/campaigns-surface'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { useAuth } from '@/contexts/AuthContext'
import { useStorySubscription } from '@/hooks/story-subscription'
import { useCrossProductNavListener } from '@/lib/xproduct-nav'
import { Breadcrumbs } from '@/components/shared/Breadcrumbs'
import { CommandPalette } from '@/components/shared/CommandPalette'
import { Toaster } from '@/components/shared/Toaster'
import { RansomwareToastListener } from '@/components/leak-sites/RansomwareToastListener'

const PRODUCTS = JSON.stringify([
  { key: 'cyberfind', label: 'CyberFind', href: 'https://cyberfind.io' },
  { key: 'intelhub', label: 'IntelHub', href: 'https://intel.cyberfind.io' },
  { key: 'community', label: 'Community', href: 'https://community.cyberfind.io' },
])

// Benchmarks nav visibility:
// - Admin users always see /benchmarks (SP1 flag retired — feature is proven).
// - Non-admin (CISO) users see /benchmarks only when VITE_BENCHMARKS_PUBLIC_LAUNCH_ENABLED=true.
// isAdmin is resolved at render time from the AuthContext so it is reactive.
// VITE_BENCHMARKS_SP1_ENABLED has been retired; remove it from the DO App Platform
// spec after this PR merges: doctl apps spec get <app-id> → remove the var →
// doctl apps update --spec spec.yaml
const BENCHMARKS_PUBLIC_LAUNCH_ENABLED =
  import.meta.env.VITE_BENCHMARKS_PUBLIC_LAUNCH_ENABLED === 'true'

type RootNavItem = {
  to: string
  icon: typeof LayoutDashboard
  label: string
}

const BENCHMARKS_NAV_ITEM: RootNavItem = { to: '/benchmarks', icon: BookOpen, label: 'Benchmarks' }

// buildNavSections is called inside RootLayout so isAdmin (reactive) gates the
// Benchmarks entry without requiring a module-level rebuild.
// KAN-736 D33 (KAN-830 PR 10) — Campaigns gets its own primary entry beside
// Threat Actors, behind VITE_CAMPAIGNS_SURFACE_ENABLED (default off).
const CAMPAIGNS_NAV_ITEM: RootNavItem = { to: '/campaigns', icon: Crosshair, label: 'Campaigns' }

export function buildNavSections(showBenchmarks: boolean, showCampaigns = false): readonly {
  readonly label: string
  readonly items: readonly RootNavItem[]
}[] {
  return [
    {
      label: 'INTELLIGENCE',
      items: [
        { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/stories', icon: Newspaper, label: 'Stories' },
        { to: '/vulnerabilities', icon: ShieldAlert, label: 'Vulnerabilities' },
        { to: '/threat-actors', icon: Users, label: 'Threat Actors' },
        ...(showCampaigns ? [CAMPAIGNS_NAV_ITEM] : []),
        { to: '/threats/leak-sites', icon: Skull, label: 'Leak Sites' },
        { to: '/threats/supply-chain', icon: Cloud, label: 'Supply Chain' },
        { to: '/map', icon: Map, label: 'Live Map' },
      ],
    },
    {
      label: 'ANALYTICS',
      items: [
        { to: '/analytics', icon: BarChart2, label: 'Analytics' },
        ...(showBenchmarks ? [BENCHMARKS_NAV_ITEM] : []),
      ],
    },
    {
      label: 'REGULATORY',
      items: [
        { to: '/regulatory', icon: FileText, label: 'Regulatory' },
        // KAN-256 — restored. The Calendar was RANGE-CAPPED, not data-starved:
        // the `window` enum was 30|90|365 while the only live deadlines are
        // ~470 and ~710 days out. PR #1207 added an unbounded `all` window and
        // made it the default, so the page renders its two EU AI Act deadlines.
        { to: '/regulatory/calendar', icon: CalendarDays, label: 'Regulatory Calendar' },
        { to: '/regulatory/compare', icon: GitCompare, label: 'Regulatory Compare' },
        { to: '/regulatory-actions', icon: Scale, label: 'Regulatory Actions' },
      ],
    },
  ]
}

const BOTTOM_NAV = [
  { to: '/admin', icon: Shield, label: 'Admin' },
  { to: '/settings', icon: Settings, label: 'Settings' },
] as const

type NavItem = { readonly to: string; readonly icon: typeof LayoutDashboard; readonly label: string }

export function shouldUseExactNavMatch(to: string): boolean {
  return to === '/' || to === '/regulatory'
}

export function shouldShowSharedBreadcrumbs(pathname: string): boolean {
  return !(pathname === '/map' || pathname.startsWith('/map/'))
}

export function RootLayout() {
  const [crossProductNav, setCrossProductNav] = useState<HTMLElement | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()
  const { user, isAdmin, logout } = useAuth()

  // Admins always see /benchmarks; non-admins need the public-launch flag.
  const showBenchmarks = isAdmin || BENCHMARKS_PUBLIC_LAUNCH_ENABLED
  const NAV_SECTIONS = buildNavSections(showBenchmarks, campaignsSurfaceEnabled())

  // Phase 1 Dedup T17: subscribe to story:* WS events while authenticated.
  useStorySubscription(Boolean(user))
  useCrossProductNavListener(crossProductNav)

  // Hide sidebar on /map?fullscreen=true
  const params = new URLSearchParams(location.search)
  const isFullscreen = location.pathname === '/map' && params.get('fullscreen') === 'true'
  if (isFullscreen) return <Outlet />

  const showSharedBreadcrumbs = shouldShowSharedBreadcrumbs(location.pathname)

  const renderNavItem = ({ to, icon: Icon, label }: NavItem) => (
    <NavLink
      key={to}
      to={to}
      end={shouldUseExactNavMatch(to)}
      onClick={() => setMobileOpen(false)}
      aria-label={collapsed ? label : undefined}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'border-primary bg-sidebar-accent text-sidebar-accent-foreground'
            : 'border-transparent text-sidebar-foreground hover:bg-sidebar-accent/50',
          collapsed && 'justify-center px-2',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  )

  return (
    // Single outer viewport column: cyberfind-nav stays pinned at the top; the
    // app shell fills the remaining height. Without this wrapper the nav (48px)
    // plus `h-screen` (100vh) overflowed the viewport, producing a second
    // scrollbar and letting the nav scroll out of view.
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      <cyberfind-nav
        ref={setCrossProductNav}
        active-product="intelhub"
        products={PRODUCTS}
        sign-in-url="/api/auth/login"
        sign-out-url="/api/auth/logout"
      />
      <CommandPalette />
      <Toaster />
      <RansomwareToastListener />
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Desktop sidebar */}
        <aside
          className={cn(
            'hidden md:flex flex-col border-r bg-sidebar transition-all duration-200',
            collapsed ? 'w-16' : 'w-60',
          )}
        >
          <div className="flex h-14 items-center justify-between px-4 border-b border-sidebar-border">
            {!collapsed && (
              <Link to="/" className="flex items-center gap-2 font-bold text-sm">
                <img
                  src="/icons/cyberfind-icon-light.svg"
                  alt=""
                  className="h-5 w-5 dark:hidden"
                  aria-hidden
                />
                <img
                  src="/icons/cyberfind-icon-dark.svg"
                  alt=""
                  className="h-5 w-5 hidden dark:block"
                  aria-hidden
                />
                IntelHub
              </Link>
            )}
            {collapsed && (
              <>
                <img
                  src="/icons/cyberfind-icon-light.svg"
                  alt=""
                  className="h-5 w-5 mx-auto dark:hidden"
                  aria-hidden
                />
                <img
                  src="/icons/cyberfind-icon-dark.svg"
                  alt=""
                  className="h-5 w-5 mx-auto hidden dark:block"
                  aria-hidden
                />
              </>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 ml-auto"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>

          <ScrollArea className="flex-1">
            <nav className="flex flex-col gap-4 p-2">
              {NAV_SECTIONS.map((section) => (
                <div key={section.label} className="flex flex-col gap-1">
                  {!collapsed && (
                    <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {section.label}
                    </div>
                  )}
                  {section.items.map(renderNavItem)}
                </div>
              ))}
            </nav>
          </ScrollArea>

          <Separator />
          <div className="flex flex-col gap-1 p-2">
            {BOTTOM_NAV.map(renderNavItem)}
            {user && (
              <button
                onClick={logout}
                className={cn(
                  'flex items-center gap-3 rounded-md border border-transparent px-3 py-2 text-sm font-medium transition-colors',
                  'text-sidebar-foreground hover:bg-sidebar-accent/50',
                  collapsed && 'justify-center px-2',
                )}
                aria-label="Sign out"
                title={collapsed ? 'Sign out' : undefined}
              >
                <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                {!collapsed && 'Sign out'}
              </button>
            )}
          </div>
        </aside>

        {/* Main content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <header className="flex md:hidden h-14 items-center border-b px-4 gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Link to="/" className="flex items-center gap-2 font-bold text-sm">
              <img
                src="/icons/cyberfind-icon-light.svg"
                alt=""
                className="h-5 w-5 dark:hidden"
                aria-hidden
              />
              <img
                src="/icons/cyberfind-icon-dark.svg"
                alt=""
                className="h-5 w-5 hidden dark:block"
                aria-hidden
              />
              IntelHub
            </Link>
          </header>

          {mobileOpen && (
            <div className="fixed inset-0 z-50 md:hidden">
              <div
                className="absolute inset-0 bg-slate-950/50"
                onClick={() => setMobileOpen(false)}
                aria-hidden
              />
              <aside className="absolute left-0 top-0 h-full w-64 bg-sidebar border-r flex flex-col">
                <div className="flex h-14 items-center px-4 border-b border-sidebar-border gap-2">
                  <img
                    src="/icons/cyberfind-icon-light.svg"
                    alt=""
                    className="h-5 w-5 dark:hidden"
                    aria-hidden
                  />
                  <img
                    src="/icons/cyberfind-icon-dark.svg"
                    alt=""
                    className="h-5 w-5 hidden dark:block"
                    aria-hidden
                  />
                  <span className="font-bold text-sm">IntelHub</span>
                </div>
                <ScrollArea className="flex-1">
                  <nav className="flex flex-col gap-4 p-2">
                    {NAV_SECTIONS.map((section) => (
                      <div key={section.label} className="flex flex-col gap-1">
                        <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {section.label}
                        </div>
                        {section.items.map(renderNavItem)}
                      </div>
                    ))}
                  </nav>
                </ScrollArea>
                {user && (
                  <div className="p-2 border-t border-sidebar-border">
                    <button
                      onClick={logout}
                      className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
                    >
                      <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                      Sign out
                    </button>
                  </div>
                )}
              </aside>
            </div>
          )}

          {showSharedBreadcrumbs && (
            <div className="px-4 md:px-6 pt-3 pb-0">
              <Breadcrumbs />
            </div>
          )}

          <main className="flex-1 overflow-y-auto">
            {/*
              KAN-32 F1. In the flow, above the page, so it is prominent
              without blocking anything — Art. 21(4) asks for "clearly and
              separately", and P1 forbids a consent banner. Renders null unless
              the build carries VITE_POSTHOG_KEY and the user has not
              acknowledged it, so it is invisible in today's production build.
            */}
            <AnalyticsNotice />
            <Outlet />
          </main>

          <OnboardingFlow />

          {/* Mobile bottom tab bar — keeps top-5 nav items */}
          <nav className="flex md:hidden border-t bg-background">
            {NAV_SECTIONS.flatMap((s) => s.items as readonly NavItem[]).slice(0, 5).map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                end={shouldUseExactNavMatch(to)}
                className={({ isActive }) =>
                  cn(
                    'flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-xs font-medium transition-colors',
                    isActive ? 'text-primary' : 'text-muted-foreground',
                  )
                }
              >
                <Icon className="h-5 w-5" aria-hidden />
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
    </div>
  )
}
